import { useState } from "react";
import { useFieldArray, type UseFormReturn } from "react-hook-form";

import { toast } from "components/ui/use-toast";

import { type IStore } from "../../../../../@types/gamification";
import { type IStoreOptionWithDetails } from "../../../../../services/store/select-options";
import { STORE_OPTION_VALUES } from "../../constants/store-options";
import {
  formatStoreAddress,
  formatStoreNickname,
} from "../../utils/store-formatters";
import { type UpdateGamificationFormData } from "../../validations/gamification.validation";

interface UseEditStoreManagerArgs {
  form: UseFormReturn<UpdateGamificationFormData>;
  storeOptionsMap: Map<string, IStoreOptionWithDetails>;
  existingStoreCodes: Set<string>;

  lockExistingStores: boolean;
}

export const useEditStoreManager = ({
  form,
  storeOptionsMap,
  existingStoreCodes,
  lockExistingStores,
}: UseEditStoreManagerArgs) => {
  const [storeCodesInput, setStoreCodesInput] = useState("");

  const {
    fields: storeFields,
    append: appendStore,
    remove: removeStore,
  } = useFieldArray({ control: form.control, name: "stores" });

  const clearAllStoresFlag = () => {
    if (!form.getValues("allStoresSelected")) return;
    form.setValue("stores", []);
    form.setValue("allStoresSelected", false);
  };

  const handleAddStore = (storeCode: string) => {
    if (storeCode === STORE_OPTION_VALUES.ALL_STORES) {
      toast({
        title: "Aviso",
        description:
          "Não é possível selecionar 'Todas as Lojas' ao editar uma campanha",
        variant: "destructive",
      });
      return;
    }

    const store = storeOptionsMap.get(storeCode);
    if (!store) return;

    clearAllStoresFlag();

    const storeName = formatStoreAddress(storeCode, storeOptionsMap) || store.label;
    const nickname = formatStoreNickname(storeCode, storeOptionsMap);
    appendStore({ storeCode: store.value, storeName, nickname });
  };

  const handleAddStoresByCodes = () => {
    if (!storeCodesInput.trim()) return;

    clearAllStoresFlag();

    const codes = Array.from(
      new Set(
        storeCodesInput
          .split(";")
          .map((code) => code.trim())
          .filter((code) => code.length > 0),
      ),
    );

    if (codes.length === 0) {
      toast({
        title: "Erro",
        description: "Nenhum código de loja válido foi informado",
        variant: "destructive",
      });
      return;
    }

    const currentStores = form.getValues("stores") || [];
    const existingCodes = currentStores.map((store) => String(store.storeCode));
    const newStores: IStore[] = [];
    const notFoundCodes: string[] = [];
    const duplicateCodes: string[] = [];

    codes.forEach((code) => {
      if (existingCodes.includes(String(code))) {
        duplicateCodes.push(code);
        return;
      }

      const store = storeOptionsMap.get(code);
      if (!store) {
        notFoundCodes.push(code);
        return;
      }

      const storeName = formatStoreAddress(code, storeOptionsMap) || store.label;
      const nickname = formatStoreNickname(code, storeOptionsMap);
      newStores.push({ storeCode: store.value, storeName, nickname });
      existingCodes.push(String(code));
    });

    if (newStores.length > 0) {
      newStores.forEach((store) => appendStore(store));
      toast({
        title: "Sucesso",
        description: `${newStores.length} loja(s) adicionada(s) com sucesso`,
      });
    }

    if (duplicateCodes.length > 0) {
      const uniqueDuplicates = Array.from(new Set(duplicateCodes));
      toast({
        title: "Aviso",
        description: `Loja(s) ${uniqueDuplicates.join(", ")} já está(ão) na lista e não foi(ram) adicionada(s) novamente`,
        variant: "destructive",
      });
    }

    if (notFoundCodes.length > 0) {
      Array.from(new Set(notFoundCodes)).forEach((code) => {
        toast({
          title: "Aviso",
          description: `Loja ${code} não encontrada`,
          variant: "destructive",
        });
      });
    }
    setStoreCodesInput("");
  };

  const handleRemoveStore = (index: number) => {
    const storeCode = String(storeFields[index].storeCode);
    if (lockExistingStores && existingStoreCodes.has(storeCode)) {
      toast({
        title: "Aviso",
        description:
          "Não é possível remover lojas que já fazem parte da campanha",
        variant: "destructive",
      });
      return;
    }
    removeStore(index);
  };

  return {
    storeFields,
    storeCodesInput,
    setStoreCodesInput,
    handleAddStore,
    handleAddStoresByCodes,
    handleRemoveStore,
  };
};
